import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { LineItemAmount, LineItemWithAmounts } from 'utils/customTypes';
import {
  getSelectedBudget,
  selectLaborOfCostLineItems,
  selectProjectCostOfLaborAmounts,
} from '../../../state/BudgetList/budgetListSlice';
import useOptions from './useOptions';
import { calculateAmountsOfLineItems } from '../helpers/budgetHelper';

const useProjectLaborOfCost = () => {
  const { filteredProjects } = useOptions();
  const costOfLabors = useSelector(selectLaborOfCostLineItems);
  const projectCostOfLaborAmounts = useSelector(
    selectProjectCostOfLaborAmounts
  );

  const currentBudget = useSelector(getSelectedBudget);

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  function generateDummyAmounts(): LineItemAmount[] {
    return Array(12)
      .fill(0)
      .map((_, index) => ({
        id: '',
        organizationId: '',
        lineItemId: '',
        month: months[index],
        monthIndex: index,
        allocation: 0,
        actual: 0,
      }));
  }

  const costOfLaborLineItems = useMemo(() => {
    if (!currentBudget) {
      return [];
    }
    const allCostOfLabors = filteredProjects.map((project) => {
      const lineItem = costOfLabors.find(
        (costOfLabor) => costOfLabor.projectId === project.id
      );

      if (lineItem) {
        return { ...lineItem };
      }
      return {
        id: '',
        description: 'Cost of Labor',
        projectId: project.id,
        amounts: generateDummyAmounts(),
        rawAmounts: Array(12).fill(0),
        rawActuals: Array(12).fill(0),
        organizationId: '',
        budgetId: '',
        showInput: false,
        currency: '',
      };
    });

    let allCostOfLaborsWithActuals = allCostOfLabors.map(
      (lineItem: LineItemWithAmounts) => {
        const matchingData = projectCostOfLaborAmounts.find(
          (laborOfCostActual) =>
            laborOfCostActual.projectId === lineItem.projectId
        );
        if (matchingData) {
          const monthlyActual = (matchingData.actual ?? 0) / 12;
          // Distribute over a full 12-slot array, not over the persisted
          // amounts. Line-item amount rows are sparse -- the API only returns
          // rows for months that have data (buildMonthlyRawValues comment) --
          // so mapping only over `lineItem.amounts` would sum to
          // `actual * (rows / 12)`. For a persisted CoL line item with 3
          // rows and a $1,200 endpoint total that rendered as $300 with 9
          // empty months. Preserve any persisted allocation values so
          // rawAmounts (allocation rollups) stays correct.
          const existingByMonth = new Map(
            lineItem.amounts.map((a) => [a.monthIndex, a])
          );
          return {
            ...lineItem,
            amounts: months.map((monthName, monthIndex) => {
              const existing = existingByMonth.get(monthIndex);
              return {
                id: existing?.id ?? '',
                organizationId:
                  existing?.organizationId ?? lineItem.organizationId ?? '',
                lineItemId: existing?.lineItemId ?? lineItem.id ?? '',
                month: existing?.month ?? monthName,
                monthIndex,
                allocation: existing?.allocation ?? 0,
                actual: monthlyActual,
              };
            }),
          };
        }
        return lineItem;
      }
    );

    return calculateAmountsOfLineItems(
      allCostOfLaborsWithActuals,
      currentBudget!
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProjects, costOfLabors, projectCostOfLaborAmounts]);

  return { costOfLaborLineItems };
};

export default useProjectLaborOfCost;
